// src/components/books/FlipbookManager.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Trash2, Upload, BookOpen, ChevronLeft, ChevronRight, 
  Eye, Loader, AlertCircle, GripVertical, Edit2, Save, X
} from 'lucide-react';
import { flipbookService } from '../../services/flipbookService';

export const FlipbookManager = ({ bookId, bookData, user }) => {
  const navigate = useNavigate();
  const [booklets, setBooklets] = useState([]);
  const [selectedBooklet, setSelectedBooklet] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [editingBooklet, setEditingBooklet] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadBooklets();
  }, [bookId]);

  useEffect(() => {
    if (selectedBooklet) {
      loadPages(selectedBooklet.id);
    }
  }, [selectedBooklet]);

  const loadBooklets = async () => {
    try {
      setLoading(true);
      setError(null);
      const bookletsData = await flipbookService.getBooklets(bookId);
      setBooklets(bookletsData);
      
      if (bookletsData.length > 0 && !selectedBooklet) {
        setSelectedBooklet(bookletsData[0]);
      }
    } catch (error) {
      console.error('Error loading booklets:', error);
      setError('Failed to load booklets');
    } finally {
      setLoading(false);
    }
  };

  const loadPages = async (bookletId) => {
    try {
      const pagesData = await flipbookService.getPages(bookId, bookletId);
      setPages(pagesData);
    } catch (error) {
      console.error('Error loading pages:', error);
      setError('Failed to load pages');
    }
  };

  const createBooklet = async () => {
    try {
      await flipbookService.createBooklet(bookId, {
        title: `Booklet ${booklets.length + 1}`
      });
      await loadBooklets();
    } catch (error) {
      console.error('Error creating booklet:', error);
      setError('Failed to create booklet');
    }
  };

  const updateBooklet = async (bookletId, updates) => {
    try {
      await flipbookService.updateBooklet(bookId, bookletId, updates);
      await loadBooklets();
      setEditingBooklet(null);
    } catch (error) {
      console.error('Error updating booklet:', error);
      setError('Failed to update booklet');
    }
  };

  const deleteBooklet = async (bookletId) => {
    if (!window.confirm('Delete this booklet and all its pages?')) return;

    try {
      await flipbookService.deleteBooklet(bookId, bookletId);
      await loadBooklets();
      setSelectedBooklet(booklets[0] || null);
    } catch (error) {
      console.error('Error deleting booklet:', error);
      setError('Failed to delete booklet');
    }
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedBooklet) return;

    try {
      setUploading(true);
      setError(null);
      
      await flipbookService.uploadPdfToBooklet(
        bookId, 
        selectedBooklet.id, 
        file,
        (progress) => setUploadProgress(progress)
      );

      await loadPages(selectedBooklet.id);
      await loadBooklets();
      setUploadProgress(null);
      alert(`Uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading PDF:', error);
      setError('Failed to upload PDF');
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Delete this page?')) return;

    try {
      await flipbookService.deletePage(bookId, selectedBooklet.id, pageId);
      await loadPages(selectedBooklet.id);
      await loadBooklets();
    } catch (error) {
      console.error('Error deleting page:', error);
      setError('Failed to delete page');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Flipbook Manager</h1>
            <p className="text-gray-600 mt-1">{bookData?.botName || 'Manage your flipbooks'}</p>
          </div>
          <button
            onClick={() => navigate(`/books/${bookId}`)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {uploadProgress && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">
                Uploading: Page {uploadProgress.current} of {uploadProgress.total}
              </span>
              <span className="text-sm text-blue-700">{uploadProgress.percentage}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.percentage}%` }}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Booklets Sidebar */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-gray-900">Booklets</h2>
              <button
                onClick={createBooklet}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              </div>
            ) : (
              <div className="space-y-2">
                {booklets.map(booklet => (
                  <div
                    key={booklet.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedBooklet?.id === booklet.id
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    {editingBooklet?.id === booklet.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editingBooklet.title}
                          onChange={(e) => setEditingBooklet({ ...editingBooklet, title: e.target.value })}
                          className="w-full px-2 py-1 border rounded"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateBooklet(booklet.id, { title: editingBooklet.title })}
                            className="flex-1 px-2 py-1 bg-green-600 text-white rounded text-sm"
                          >
                            <Save className="w-3 h-3 mx-auto" />
                          </button>
                          <button
                            onClick={() => setEditingBooklet(null)}
                            className="flex-1 px-2 py-1 bg-gray-600 text-white rounded text-sm"
                          >
                            <X className="w-3 h-3 mx-auto" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div onClick={() => setSelectedBooklet(booklet)}>
                          <h3 className="font-medium text-sm">{booklet.title}</h3>
                          <p className="text-xs text-gray-500">{booklet.pageCount || 0} pages</p>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingBooklet(booklet);
                            }}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          >
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBooklet(booklet.id);
                            }}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pages Content */}
          <div className="lg:col-span-3 bg-white rounded-lg shadow p-6">
            {selectedBooklet ? (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">{selectedBooklet.title}</h2>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {uploading ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {pages.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {pages.map((page, index) => (
                      <div key={page.id} className="relative group">
                        <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={page.imageUrl}
                            alt={`Page ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => deletePage(page.id)}
                            className="p-2 bg-red-600 text-white rounded-full shadow-lg hover:bg-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-center mt-2 text-gray-600">Page {index + 1}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No pages yet</p>
                    <p className="text-sm text-gray-500 mt-2">Upload a PDF to get started</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Select a booklet to manage pages</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};